import React, { useState, useEffect, useRef } from 'react';
import { keywordService } from '../services/keywordService';

interface KeywordHTMLProps {
  html: string;
  className?: string;
  highlightingEnabled?: boolean;
}

// Track which keywords have been highlighted across all instances in the tutorial
// This is module-level to persist across component instances, but we'll reset it when tutorial changes
let highlightedKeywords = new Set<string>();

// Export a function to reset highlighted keywords (called when tutorial changes)
export const resetHighlightedKeywords = () => {
  highlightedKeywords.clear();
};

// Make it available globally for easy access
if (typeof window !== 'undefined') {
  (window as any).__resetHighlightedKeywords = resetHighlightedKeywords;
}

const KeywordHTML: React.FC<KeywordHTMLProps> = ({ html, className = '', highlightingEnabled = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredKeyword, setHoveredKeyword] = useState<{ word: string; definition: any; element: HTMLElement } | null>(null);

  useEffect(() => {
    console.log('KeywordHTML: useEffect triggered');
    console.log('KeywordHTML: Service enabled:', keywordService.isEnabled());
    console.log('KeywordHTML: Highlighting enabled:', highlightingEnabled);
    console.log('KeywordHTML: Definitions count:', keywordService.getAllDefinitions().length);
    
    if (!keywordService.isEnabled() || !highlightingEnabled) {
      console.log('KeywordHTML: Service not enabled or highlighting disabled, skipping');
      return;
    }

    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;
    
    // Check if keywords have already been processed
    if (container.querySelector('[data-keyword]')) {
      return;
    }

    // Use TreeWalker to process text nodes
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Node | null;

    // Collect all text nodes
    while (node = walker.nextNode()) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        textNodes.push(node as Text);
      }
    }

    // Process each text node
    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      console.log('KeywordHTML: Processing text:', text.substring(0, 50) + '...');
      const keywords = keywordService.findKeywordsInText(text);
      console.log('KeywordHTML: Found keywords:', keywords.length);
      
      if (keywords.length === 0) return;

      // Create a document fragment to hold the new content
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      // Sort keywords by start index to process them in order
      const sortedKeywords = keywords.sort((a, b) => a.startIndex - b.startIndex);

      sortedKeywords.forEach(({ word, definition, startIndex, endIndex }) => {
        // Create a unique key for this keyword definition (use definition ID to track across instances)
        const keywordKey = definition.id || definition.word.toLowerCase();
        
        // Skip if this keyword has already been highlighted in this tutorial
        if (highlightedKeywords.has(keywordKey)) {
          // Add as plain text instead of highlighting
          if (startIndex > lastIndex) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex, startIndex)));
          }
          fragment.appendChild(document.createTextNode(word));
          lastIndex = endIndex;
          return;
        }

        // Mark this keyword as highlighted
        highlightedKeywords.add(keywordKey);

        // Add text before the keyword
        if (startIndex > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, startIndex)));
        }

        // Create the keyword element with tooltip
        const keywordSpan = document.createElement('span');
        keywordSpan.className = 'inline-flex items-center cursor-help text-blue-600 hover:text-blue-800 underline decoration-dotted underline-offset-2 transition-colors';
        keywordSpan.textContent = word;
        keywordSpan.setAttribute('data-keyword', word);
        keywordSpan.setAttribute('data-definition', JSON.stringify(definition));
        keywordSpan.setAttribute('data-category', definition.category || '');
        keywordSpan.setAttribute('data-explanation', definition.explanation);
        
        // Add info icon
        const infoIcon = document.createElement('span');
        infoIcon.innerHTML = '<svg class="w-3 h-3 ml-1 opacity-60" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>';
        keywordSpan.appendChild(infoIcon);

        // Add event listeners directly to the keyword element
        keywordSpan.addEventListener('mouseenter', () => {
          console.log('KeywordHTML: Mouse enter on keyword:', word);
          setHoveredKeyword({ word, definition, element: keywordSpan });
        });

        keywordSpan.addEventListener('mouseleave', () => {
          console.log('KeywordHTML: Mouse leave on keyword:', word);
          setHoveredKeyword(null);
        });

        fragment.appendChild(keywordSpan);
        lastIndex = endIndex;
      });

      // Add remaining text
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      // Replace the original text node with the fragment
      if (textNode.parentNode) {
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    });
  }, [html, highlightingEnabled]);

  return (
    <>
      <div ref={containerRef} className={className} dangerouslySetInnerHTML={{ __html: html }} />
      
      {hoveredKeyword && (
        <div 
          className="fixed z-50 bg-gray-900 text-white rounded-lg shadow-xl p-4 max-w-xs border border-gray-700 pointer-events-none"
          style={{
            left: `${hoveredKeyword.element.getBoundingClientRect().left + hoveredKeyword.element.getBoundingClientRect().width / 2}px`,
            top: `${hoveredKeyword.element.getBoundingClientRect().top - 10}px`,
            transform: 'translateX(-50%) translateY(-100%)'
          }}
        >
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
            </svg>
            <div>
              <div className="font-semibold text-blue-400 mb-1">{hoveredKeyword.word}</div>
              {hoveredKeyword.definition.category && (
                <div className="text-xs text-gray-400 mb-2 capitalize">{hoveredKeyword.definition.category}</div>
              )}
              <div className="text-sm text-gray-200 leading-relaxed">{hoveredKeyword.definition.explanation}</div>
            </div>
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2">
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default KeywordHTML;
